import React from "react";
import { FaExternalLinkAlt, FaHubspot } from "react-icons/fa";
import { formatNumber } from "../selectors";

const SNAPSHOT_ROWS = [
  ["Plan", "addy_plan"],
  ["Status", "addy_plan_status"],
  ["LOS", "addy_los"],
  ["LOS status", "addy_los_status"],
  ["Integrations", "addy_integrations"],
  ["Chrome extension", "addy_chrome_extension_installed"],
  ["Team accepted", "addy_team_accepted_count"],
  ["Invites pending", "addy_team_pending_count"],
  ["Auto-run", "addy_autorun_enabled"],
];

const money = (amount) =>
  amount === null || amount === undefined
    ? "—"
    : `$${Number(amount).toLocaleString()}`;

const dayLabel = (ms) => {
  if (!ms) return "no close date";
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

/** "in 19 days" / "143 days overdue" — never a negative day count. */
function closeTiming(deal) {
  if (!deal?.closeDate) return null;
  const days = Math.round((deal.closeDate - Date.now()) / (24 * 60 * 60 * 1000));
  if (deal.isClosed) return null;
  return days >= 0 ? `in ${days} days` : `${Math.abs(days)} days overdue`;
}

/**
 * The CRM half of a client drill-in.
 *
 * The snapshot block is always present — it comes from Firestore and costs
 * nothing. The deal block is the live HubSpot lookup, which is why it has its
 * own loading and failure states: "we could not reach HubSpot" and "this
 * client has no deal" are different answers and the panel says which.
 */
export function CrmPanel({ snapshot, crm, loading, onRetry }) {
  const rows = SNAPSHOT_ROWS.filter(
    ([, key]) => snapshot?.[key] !== undefined && snapshot?.[key] !== "",
  );

  return (
    <section className="ct-detail-panel">
      <header className="ct-detail-head">
        <h4>
          <FaHubspot aria-hidden="true" /> HubSpot
        </h4>
        {crm?.companyId ? (
          <a
            className="ct-detail-link"
            href={`https://app.hubspot.com/contacts/objects/0-2/${crm.companyId}/view`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open company <FaExternalLinkAlt aria-hidden="true" />
          </a>
        ) : null}
      </header>

      <div className="ct-crm">
        {rows.length ? (
          <dl className="ct-crm-grid">
            {rows.map(([label, key]) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd>{snapshot[key]}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="ct-crm-note">
            No HubSpot properties have been synced for this organization yet.
          </p>
        )}

        <div className="ct-crm-deal">
          {loading ? (
            <p className="ct-crm-note">Looking up the live deal in HubSpot…</p>
          ) : !crm ? (
            <p className="ct-crm-note">
              Live deal not loaded.{" "}
              <button type="button" className="ct-linkbtn" onClick={onRetry}>
                Fetch from HubSpot
              </button>
            </p>
          ) : crm.primaryDeal ? (
            <>
              <div className="ct-crm-deal-head">
                <span className="ct-crm-deal-name">{crm.primaryDeal.name}</span>
                <span
                  className={`ct-crm-stage${crm.primaryDeal.isClosed ? " ct-crm-stage--closed" : ""}`}
                >
                  {crm.primaryDeal.stage || "unknown stage"}
                </span>
              </div>
              <dl className="ct-crm-grid">
                <div>
                  <dt>Amount</dt>
                  <dd>{money(crm.primaryDeal.amount)}</dd>
                </div>
                <div>
                  <dt>Close date</dt>
                  <dd>
                    {dayLabel(crm.primaryDeal.closeDate)}
                    {closeTiming(crm.primaryDeal) ? (
                      <span className="ct-sub-value"> · {closeTiming(crm.primaryDeal)}</span>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt>Deal owner</dt>
                  <dd>{crm.primaryDeal.owner || "unassigned"}</dd>
                </div>
                <div>
                  <dt>Matched via</dt>
                  <dd title={crm.anchorEmail || ""}>{crm.anchorSource || "—"}</dd>
                </div>
              </dl>
              {crm.deals?.length > 1 ? (
                <p className="ct-crm-note">
                  {formatNumber(crm.deals.length - 1)} other deal
                  {crm.deals.length === 2 ? "" : "s"} attached to this company.
                </p>
              ) : null}
            </>
          ) : (
            <p className="ct-crm-note">{crm.reason || "No deal found in HubSpot."}</p>
          )}
        </div>
      </div>
    </section>
  );
}
